import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Tag } from "lucide-react";
import { RequestReceived } from "@/pages/BookingResult";
import api, { apiError } from "@/lib/api";
import { SEO, Eyebrow, EASE } from "@/components/motion";
import { SERVICE_ALIASES, computeQuote, londonDate, gbp } from "@/lib/booking";
import { StepService, StepProperty, StepFrequency } from "@/components/booking/StepsChoice";
import { StepExtras } from "@/components/booking/StepExtras";
import { StepSchedule, StepDetails } from "@/components/booking/StepsForm";
import { StepReview, PriceBar } from "@/components/booking/StepReview";

const LABELS = { service: "Service", property: "Property", frequency: "Frequency", extras: "Extras", schedule: "Date & time", details: "Details", review: "Review" };

export default function Book() {
  const [params] = useSearchParams();
  const paramService = params.get("service") || "";
  const [catalog, setCatalog] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [promo, setPromo] = useState(null);
  const [autoCode, setAutoCode] = useState((params.get("promo") || "").trim().toUpperCase().slice(0, 30));
  const [form, setForm] = useState({
    service_slug: SERVICE_ALIASES[paramService] || paramService, property_key: "", frequency: "", extras: [],
    date: "", time_slot: "", name: "", email: "", phone: "", address: "", postcode: "",
    access_instructions: "", notes: "", parking_review: false, terms_accepted: false,
  });

  useEffect(() => {
    Promise.all([api.get("/pricing"), api.get("/availability")])
      .then(([p, a]) => {
        setCatalog(p.data);
        setBlocked(a.data);
        setForm((f) => (p.data.services.some((s) => s.slug === f.service_slug) ? f : { ...f, service_slug: "" }));
      })
      .catch(() => setError("We couldn't load our prices. Please refresh the page."));
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const q = useMemo(() => computeQuote(catalog, form), [catalog, form]);
  const cartKey = `${form.service_slug}|${form.property_key}|${form.frequency}|${form.extras.join(",")}|${form.email}`;
  useEffect(() => setPromo(null), [cartKey]);
  const total = promo ? promo.total_price : q.total;
  const steps = useMemo(
    () => ["service", "property", ...(q.service?.has_frequency ? ["frequency"] : []), "extras", "schedule", "details", "review"],
    [q.service]
  );
  const current = steps[step];
  const minDate = form.extras.includes("same-day") ? londonDate(0) : londonDate(1);

  const validate = () => {
    if (current === "service" && !q.service) return "Please choose a service.";
    if (current === "property" && !q.property) return "Please select your property.";
    if (current === "frequency" && !form.frequency) return "Please choose weekly or fortnightly.";
    if (current === "schedule") {
      if (!form.date) return "Please choose a preferred date.";
      if (form.date < minDate) return form.date === londonDate(0) ? "For a booking today, please add the Same-day / urgent booking extra." : "Please choose a date from tomorrow onwards.";
      if (blocked.includes(form.date)) return "That date is fully booked - please choose another.";
      if (!form.time_slot) return "Please choose a preferred arrival window.";
    }
    if (current === "details") {
      if (!form.name.trim()) return "Please enter your full name.";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email address.";
      if (form.phone.replace(/\D/g, "").length < 7) return "Please enter your mobile number.";
      if (!form.address.trim()) return "Please enter the full property address.";
      if (!form.postcode.trim()) return "Please enter the postcode.";
      if (!form.terms_accepted) return "Please accept the Terms & Conditions to continue.";
    }
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) return setError(err);
    setError("");
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const [requestedRef, setRequestedRef] = useState("");
  const pay = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/bookings/request", {
        ...form, frequency: form.frequency || null, promo_code: promo?.promo_code || null, origin_url: window.location.origin,
      });
      setRequestedRef(data.booking_ref);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (requestedRef) return <RequestReceived bookingRef={requestedRef} email={form.email} />;

  return (
    <main data-testid="booking-page" className="mx-auto max-w-3xl px-5 sm:px-8 pt-32 sm:pt-40 pb-24">
      <SEO
        title="Book a Clean | Fixed Prices | HOMELARA London"
        description="Book your HOMELARA clean online in a few simple steps - fixed prices for regular, one-off, deep, Airbnb and end of tenancy cleaning across London Zones 1 & 2."
      />
      <Eyebrow>Book a Clean</Eyebrow>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-ink">Book your clean.</h1>
      <p className="mt-4 max-w-lg text-sm sm:text-base leading-relaxed text-body">
        Fixed prices per property. See your full price, then send us your booking request - no payment is taken until we confirm.
      </p>

      {paramService === "business-cleaning" && (
        <p data-testid="booking-business-note" className="mt-6 rounded-2xl border border-sage/40 bg-sage-light/50 p-5 text-sm leading-relaxed text-body">
          Business cleaning is tailored to each space. Please <Link to="/contact" className="text-blue underline underline-offset-4">get in touch</Link> and we'll prepare a proposal.
        </p>
      )}

      {autoCode && (
        <p data-testid="booking-promo-link-note" className="mt-6 flex items-center gap-3 rounded-2xl border border-sage bg-sage-light/70 px-5 py-4 text-sm leading-relaxed text-body">
          <Tag size={16} className="shrink-0 text-sage-dark" />
          <span>Promo code <span className="font-mono text-xs tracking-[0.1em] text-blue">{autoCode}</span> will be applied automatically at the review step.</span>
        </p>
      )}

      <div className="mt-10" data-testid="booking-progress">
        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.15em] text-faint">
          <span>Step {step + 1} of {steps.length}</span>
          <span data-testid="booking-step-label">{LABELS[current]}</span>
        </div>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-subtle">
          <motion.div className="h-full bg-blue" animate={{ width: `${((step + 1) / steps.length) * 100}%` }} transition={{ duration: 0.5, ease: EASE }} />
        </div>
      </div>

      <div className="mt-10 rounded-3xl border border-subtle bg-surface p-6 sm:p-10">
        {!catalog ? (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-faint">{error || "Loading prices..."}</p>
        ) : (
          <>
            <AnimatePresence mode="wait">
              <motion.div key={current} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease: EASE }}>
                {current === "service" && <StepService catalog={catalog} form={form} set={set} />}
                {current === "property" && <StepProperty service={q.service} form={form} set={set} />}
                {current === "frequency" && <StepFrequency catalog={catalog} form={form} set={set} />}
                {current === "extras" && <StepExtras catalog={catalog} service={q.service} form={form} set={set} />}
                {current === "schedule" && <StepSchedule form={form} set={set} minDate={minDate} blocked={blocked} />}
                {current === "details" && <StepDetails form={form} set={set} />}
                {current === "review" && (
                  <StepReview form={form} q={q} catalog={catalog} promo={promo} setPromo={setPromo}
                    autoCode={autoCode} onPromoRemove={() => setAutoCode("")} />
                )}
              </motion.div>
            </AnimatePresence>

            {current !== "review" && <PriceBar q={q} />}
            {error && <p data-testid="booking-error" className="mt-6 text-sm text-red-700">{error}</p>}

            <div className="mt-8 flex items-center justify-between gap-3">
              <button type="button" onClick={back} disabled={step === 0} data-testid="booking-back-button"
                className="inline-flex items-center gap-2 rounded-full border border-subtle px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.15em] text-ink transition-colors hover:border-blue hover:text-blue disabled:opacity-0">
                <ArrowLeft size={14} /> Back
              </button>
              {current !== "review" ? (
                <button type="button" onClick={next} data-testid="booking-next-button"
                  className="inline-flex items-center gap-2 rounded-full bg-blue px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.15em] text-canvas transition-all hover:bg-blue-hover hover:-translate-y-0.5">
                  Continue <ArrowRight size={14} />
                </button>
              ) : (
                <button type="button" onClick={pay} disabled={submitting} data-testid="booking-request-button"
                  className="inline-flex items-center gap-2 rounded-full bg-blue px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.15em] text-canvas transition-all hover:bg-blue-hover hover:-translate-y-0.5 disabled:opacity-60">
                  {submitting ? "Sending..." : "Request a Booking"} <ArrowRight size={14} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
